import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import rootReducer from './reducers/rootReducer';
import { LademittelModel } from '../models/LademittelModel';
import { VerpackungModel } from '../models/VerpackungModel';
import { AuftragModel } from '../models/AuftragModel';
import { ServiceModel } from '../models/ServiceModel';
import { AuftragPositionModel } from '../models/AuftragPositionModel';
import {
  Adresse,
  Auftrag,
  Auftragsvorlage,
  Benutzer,
  Benutzerversion,
  Mandant,
  MandantSummary,
  MautberechnungParam,
  Message,
  Sendungsstatus,
  Sprache,
  Translation,
} from '../api';
import { WaehrungModel } from '../models/WaehrungModel';
import { ZusLademittelModel } from '../models/ZusLademittelModel';
import { EigentuemerModel } from '../models/EigentuemerModel';
import { TerminkennzeichenModel } from '../models/TerminkennzeichenModel';
import { AdresseModel } from '../models/AdresseModel';
import { LandModel } from '../models/LandModel';
import { ReportModel } from '../models/ReportModel';
import { ZustellkennzeichenModel } from '../models/ZustellkennzeichenModel';
import { AnlieferkennzeichenModel } from '../models/AnlieferkennzeichenModel';
import { FrankaturModel } from '../models/FrankaturModel';
import { VerkehrsartModel } from '../models/VerkehrsartModel';
import { UserSessionModel } from '../models/UserSessionModel';
import { MetrokennungModel } from '../models/MetrokennungModel';
import { SperrigkeitModel } from '../models/SperrigkeitModel';
import { StatusModel } from '../models/StatusModel';
import { AuthModel } from '../models/AuthModel';
import { ConfigModel } from '../models/ConfigModel';
import { BerechnteilModel } from '../models/BerechnteilModel';
import { DokumentenModel } from '../models/DokumentenModel';
import { AuftragDefaultServiceModel } from '../models/AuftragDefaultServiceModel';
import { AuftragServiceModel } from '../models/AuftragServiceModel';

const initialState = {};

const middleware = [thunk];
export type storeType = typeof store;
export const store = createStore(
  rootReducer,
  initialState,
  composeWithDevTools(applyMiddleware(...middleware))
);

// set up a store subscription listener
// to store the users token in localStorage

// initialize current state from redux store for subscription comparison
// preventing undefined error
let currentState = store.getState();

store.subscribe(() => {
  // keep track of the previous and current state to compare changes
  const previousState = currentState;
  currentState = store.getState();
});

export default store;

export interface StateModel {
  Config: ConfigModel;
  Notifications: {
    messageList: Message[];
    newNotification: boolean;
    activeComponent: string;
  };
  Initialization: StateModel_Initialization;
  Stammdaten: {
    lademittel: LademittelModel[];
    verpackungen: VerpackungModel[];
    waehrungen: WaehrungModel[];
    eigentuemer: EigentuemerModel[];
    terminkennzeichen: TerminkennzeichenModel[];
    zustellkennzeichen: ZustellkennzeichenModel[];
    anlieferkennzeichen: AnlieferkennzeichenModel[];
    laender: LandModel[];
    suchvorschlaege: [];
    reports: ReportModel[];
    frankaturen: FrankaturModel[];
    verkehrsarten: VerkehrsartModel[];
    sprachen: Sprache[];
    mandanten: MandantSummary[];
    metrokennungen: MetrokennungModel[];
    sperrigkeiten: SperrigkeitModel[];
    defaultstatuskette: Sendungsstatus[];
    stati: StatusModel[];
    auftragservice: AuftragDefaultServiceModel[];
    dynamicServices: AuftragDefaultServiceModel[];
  };
  Routing: {
    activeTab: string;
  };
  Adressen: {
    adressenliste: Adresse;
    reloadToggle: boolean;
    totalSearchResults: undefined;
  };
  Benutzer: {
    benutzerliste: Benutzer[];
    reloadToggle: boolean;
    totalSearchResults: undefined;
  };
  Auftragsvorlagen: StateModel_Auftragsvorlagen;
  Auftraege: {
    auftragsliste: AuftragModel[];
    reloadToggle: boolean;
    mandant: undefined;
    toggleSort: boolean;
    totalSearchResults: undefined;
    dokumente: DokumentenModel[];
  };
  Benutzererfassung: StateModel_Benutzererfassung;
  Auftragserfassung: StateModel_Auftragserfassung;
  Auftragsvorlageerfassung: StateModel_Auftragsvorlageerfassung;
  Adresserfassung: AdresseModel;
  Auth: AuthModel;
  UserSession: UserSessionModel;
  Modal: StateModel_Modal;
  Translations: Translation[];
  Registrierung: StateModel_Registrierung;
  Maut: StateModel_Maut;
  Admin: StateModel_Admin;
  Loading: StateModel_Loading;
}

export interface StateModel_Initialization {
  isInitialized: boolean;
}

export interface StateModel_Auftragsvorlagen {
  auftragsvorlageliste: Auftragsvorlage[];
  reloadToggle: boolean;
  totalSearchResults: undefined;
}

export interface StateModel_Loading {
  loading: boolean;
}

export interface StateModel_Modal {
  config: {
    show: false;
    children: undefined;
    height: undefined;
    width: undefined;
    title: undefined;
    description: undefined;
  };
}

export interface StateModel_Registrierung {
  laender: string[];
  suchvorschlaege: StateModel_Registrierung_Suchvorschlag[];
  config: ConfigModel;
}

export interface StateModel_Maut {
  mautlaender: LandModel[];
  mautbetrag: string;
  message: string;
  param: MautberechnungParam[];
  anfrageGestartet: boolean;
}
export interface StateModel_Admin {
  hauptMandanten: Mandant[];
  currentUnterMandanten: Mandant[];
  freieMandanten: Mandant[];
  reloadHauptmandanten: boolean;
  reloadUntermandanten: boolean;
  anzeigeStati: Sendungsstatus[];
  currentSendungsStati: Sendungsstatus[];
  freieSendungsStati: Sendungsstatus[];
  reloadSendungsStati: boolean;
  adrImportMandanten: Mandant[];
  log: string;
  reloadServerlog: boolean;
  sessionCount: number;
}

export interface StateModel_Registrierung_Suchvorschlag {
  id: number;
  lkz: string;
  ortsname: string;
  plz: string;
}

export interface StateModel_Auftragserfassung {
  id?: number;
  copyId?: number;
  adressen: AdresseModel[];
  spedeigennr?: string;
  partnerreferenz?: string;
  datum?: string;
  currentServices: ServiceModel[];
  currentControlling: any;
  currentAuftragpos: AuftragPositionModel[];
  resetToggle: boolean;
  suchvorschlaege: [];
  beladedatum?: string;
  beladezeitVon?: string;
  beladezeitBis?: string;
  empfaengerReferenz?: string;
  entladedatum?: string;
  mandant?: MandantSummary;
  entladezeitVon?: string;
  entladezeitBis?: string;
  absenderreferenz?: string;
  currentZusLademittel: ZusLademittelModel[];
  empfaengerreferenz?: string;
  terminkennzeichen?: number;
  zustellkennzeichen?: number;
  anlieferkennzeichen?: number;
  reports: ReportModel[];
  frankatur?: number;
  verkehrsart?: number;
  metrokennung?: number;
  sperrigkeit?: number;
  iban?: string;
  bic?: string;
  nachnahmeBetrag?: string;
  nachnahmeWaehrung?: WaehrungModel;
  status?: StatusModel;
  tauschpaletteA?: string;
  tauschpaletteB?: string;
  tauschpaletteR?: string;
  tauschpaletteY?: string;
  hinweis?: string;
  dispohinweis?: string;
  zollvermerk?: string;
  mitGefahrguetern?: string;
  lenkeravis?: string;
  abtragen?: string;
  myDelivery?: string;
  adv3?: string;
  vorlageAuftragId: number | undefined;
  isCreateAbsender: boolean;
  isCreateEmpfaenger: boolean;
  kf_id?: number;
  berechnteile?: BerechnteilModel[];
  abweichendeBeladung?: boolean;
  neutralerAbsenderAdresse?: number;
  isAbweichendeBeladeAdresse?: boolean | undefined;
  isNeutralerAbsenderAdresse?: boolean | undefined;
  spotpreisnr?: string;
  currentBenutzer: Benutzer | undefined;
  erfasser: Benutzer | undefined;
  aenderer: Benutzer | undefined;
  datumErfasst?: string;
  datumgeaendert?: string;
  auftragServices: AuftragServiceModel[]; //#84367
}

export interface StateModel_Benutzererfassung {
  id?: number;
  name?: string;
  email?: string;
  password?: string;
  passwordRepeat?: string;
  sprache: Sprache;
  benutzerversion: Benutzerversion;
  usernameExists?: boolean;
  created?: Date;
  authorized?: boolean;
  main?: boolean;
  admin?: boolean;
  invisibleEinmaladresse?: boolean;
  edit?: boolean;
  rowsInGrid?: number;
}

export interface StateModel_Auftragsvorlageerfassung
  extends Omit<Auftragsvorlage, 'auftrag'> {
  exists: Auftragsvorlage[];
  auftrag?: Auftrag;
}

export interface StateModel_Gefahrgutklasse {
  unnr: {
    id: number;
    unnr: string;
  };
  currentRow: number | undefined;
}
